<script setup>
import AppearanceToggle from '@/components/AppearanceToggle.vue';
import AppLogo from '@/components/AppLogo.vue';
import CookieConsentBanner from '@/components/CookieConsentBanner.vue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link } from '@inertiajs/vue3';
import { computed, ref } from 'vue';

const loginOpen = ref(false);
const schoolSlug = ref('');
const slugError = ref('');

const slugify = (value) => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/g, '');
};

const normalizedSlug = computed(() => slugify(schoolSlug.value));

const centralHostname = computed(() => (
  typeof window === 'undefined' ? '' : window.location.hostname
));

const buildTenantLoginUrl = (slug) => {
  const { protocol, hostname, port } = window.location;
  const hostWithPort = port ? `${hostname}:${port}` : hostname;

  return `${protocol}//${slug}.${hostWithPort}/login`;
};

const openLoginDialog = () => {
  schoolSlug.value = '';
  slugError.value = '';
  loginOpen.value = true;
};

const submitLogin = () => {
  const slug = normalizedSlug.value;

  if (slug.length < 1) {
    slugError.value = 'Introduce el identificador de tu centro.';
    return;
  }

  slugError.value = '';
  window.location.assign(buildTenantLoginUrl(slug));
};
</script>

<template>
  <div class="min-h-screen bg-background text-foreground">
    <header class="w-full">
      <div class="container mx-auto px-4 py-8 md:px-8">
        <div class="grid grid-cols-1 items-center gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:gap-8">
          <div class="flex items-center gap-2">
            <Link
              href="/"
              class="text-2xl font-bold"
            >
              <AppLogo />
            </Link>
            <AppearanceToggle />
          </div>
          <nav class="flex items-center justify-end gap-4 md:gap-8">
            <Link
              href="/plan"
              class="text-sm text-muted-foreground transition-colors bg-primary text-primary-foreground px-4 py-2 rounded-md font-medium"
            >
              Únete gratis
            </Link>
            <button
              type="button"
              class="cursor-pointer text-sm text-muted-foreground transition-colors hover:text-foreground"
              @click="openLoginDialog"
            >
              Iniciar sesión
            </button>
          </nav>
        </div>
      </div>
    </header>

    <main>
      <slot />
    </main>

    <footer class="mt-20">
      <div class="container mx-auto px-4">
        <div class="text-center">
          <p
            class="mb-6 text-muted-foreground"
            style="display: none;"
          >
            Potenciando a los educadores con herramientas de evaluación modernas
          </p>
          <div class="flex justify-center space-x-6 text-sm">
            <Link
              href="/privacy-policy"
              class="text-muted-foreground transition-colors hover:text-foreground"
            >
              Política de Privacidad
            </Link>
            <Link
              href="/terms-and-conditions"
              class="text-muted-foreground transition-colors hover:text-foreground"
            >
              Términos de Servicio
            </Link>
            <a
              href="mailto:ayuda@evanota.com"
              class="text-muted-foreground transition-colors hover:text-foreground"
            >
              Soporte
            </a>
            <a
              href="https://instagram.com/evanota.app"
              target="_blank"
              rel="noopener noreferrer"
              class="text-muted-foreground transition-colors hover:text-foreground"
            >
              @evanota.app
            </a>
          </div>
          <div class="my-8">
            <p class="text-sm text-muted-foreground">
              © {{ new Date().getFullYear() }} evanota. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>
    </footer>

    <Dialog v-model:open="loginOpen">
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Iniciar sesión</DialogTitle>
          <DialogDescription>
            Introduce el identificador de tu centro para ir a la página de acceso.
          </DialogDescription>
        </DialogHeader>

        <form
          class="space-y-4"
          @submit.prevent="submitLogin"
        >
          <div class="space-y-2">
            <Label for="school-slug">Identificador del centro</Label>
            <Input
              id="school-slug"
              v-model="schoolSlug"
              type="text"
              autocomplete="organization"
              placeholder="mi-colegio"
              autofocus
            />
            <p
              v-if="slugError"
              class="text-sm text-destructive"
            >
              {{ slugError }}
            </p>
            <p
              v-else-if="normalizedSlug && centralHostname"
              class="break-all text-sm text-muted-foreground"
            >
              Irás a {{ normalizedSlug }}.{{ centralHostname }}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              class="cursor-pointer"
            >
              Login
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <CookieConsentBanner />
  </div>
</template>
